import { ExternalLink, Heading } from '@ui/themed';
import { t } from 'i18next';
import { isShopify, useEnv } from '../../../context/env-context';
import { useSettings } from '../../../context/settings-context';
import { I18N_COMPONENTS } from '../constants';
import { findImage, findMetaDataEntry } from '../product-metadata';
import type { Product } from '../types/product';
import { DetailGrid } from './detail-grid';
import { ProductImage } from './product-image';

export function ProductParentCard({ product }: { product: Product }) {
  const env = useEnv();
  const { useProductsV2 } = useSettings();

  const platform = env.connected_platform;
  const externalId = product.identifiers.platform ?? '';

  const productUrl = isShopify(platform)
    ? `https://${env.store_id}/admin/products/${externalId}`
    : (product.links?.admin_url ?? '');

  const showExternalLink = isShopify(platform) || productUrl !== '';

  const productImage = findImage(product, platform);

  const options = product.option_groups
    .map((group) => {
      const metadata = findMetaDataEntry(
        product.metadata,
        group.metadata_namespace,
        group.metadata_key,
      );
      return metadata ? String(metadata.value) : undefined;
    })
    .filter(Boolean)
    .join(', ');

  const details: Record<string, string> = {
    [t(`${I18N_COMPONENTS}.parent_card.product_name`)]: product.title,
    [t(`${I18N_COMPONENTS}.parent_card.id_slug`)]: product.slug,
    [t(`${I18N_COMPONENTS}.parent_card.external_id`)]: externalId,
  };
  if (product.identifiers.internal) {
    details[t(`${I18N_COMPONENTS}.parent_card.internal_id`)] = product.identifiers.internal;
  }
  if (options) {
    details[t(`${I18N_COMPONENTS}.parent_card.product_options`)] = options;
  }

  const platformLabel = platform ? t(`labels:platforms.${platform}`) : '';

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Heading style="sm" level="h2">
          {t(`${I18N_COMPONENTS}.parent_card.title`)}
        </Heading>
        {showExternalLink && platformLabel && (
          <ExternalLink link={productUrl} showIcon>
            {t(`${I18N_COMPONENTS}.parent_card.view_in_platform`, { platform: platformLabel })}
          </ExternalLink>
        )}
      </div>
      <div className="flex items-center gap-x-6 rounded-xl border border-gray-200 bg-white p-5 px-6 shadow">
        {useProductsV2 && <ProductImage src={productImage} size="lg" />}
        <DetailGrid details={details} />
      </div>
    </section>
  );
}
